// Lib
import { useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { defer } from 'lodash';

// Types
import { Editor } from '@tiptap/react';
import { TiptapContent } from '../tiptapTypes';
import { propIn } from '../../utils/immutableHelper';

const selectIsUndoing = propIn(['undoRedo', 'isUndoing']);
const selectIsRedoing = propIn(['undoRedo', 'isRedoing']);

export enum PersistedContentSyncMode {
    /**
     * Waits for the current call stack to finish before syncing to avoid overlapping editor transactions.
     */
    DEFERRED,
    /**
     * Syncs in a layout effect so the latest persisted content is available before the browser paints.
     */
    IMMEDIATE,
    /**
     * Leaves persisted-content synchronisation to the caller.
     */
    DISABLED,
}

/**
 * When changes to the persisted content occur, this will reflect those
 * changes in the editor, provided the editor isn't currently being edited or it's
 * as a result of an undo/redo.
 *
 * When it's being edited, the internal state of the editor will be used instead.
 */
const useTiptapEditorPersistedContentSync = (
    editor: Editor,
    persistedContent: TiptapContent | null,
    isEditing: boolean,
    syncMode = PersistedContentSyncMode.DEFERRED,
) => {
    const isUndoingOrRedoing = useSelector((state) => selectIsUndoing(state) || selectIsRedoing(state));

    useLayoutEffect(() => {
        if (syncMode !== PersistedContentSyncMode.IMMEDIATE) return;

        // Don't update the editor if it's currently being edited - unless it's due to a redo/undo
        if (isEditing && !isUndoingOrRedoing) return;

        editor.commands.syncFromExternalStore(persistedContent);
    }, [editor, persistedContent, syncMode]);

    useEffect(() => {
        if (syncMode !== PersistedContentSyncMode.DEFERRED) return;

        // Don't update the editor if it's currently being edited - unless it's due to a redo/undo
        if (isEditing && !isUndoingOrRedoing) return;

        // We need to defer this, in case the persisted content update is a result
        // of another editor update (while the editor isn't being edited), e.g.
        // during a clipper operation.
        // Otherwise, it will result in a mismatched transaction because a new chain will
        // be started while the other one is still in progress.
        defer(() => {
            editor.commands.syncFromExternalStore(persistedContent);
        });
    }, [persistedContent, syncMode]);
};

export default useTiptapEditorPersistedContentSync;
